import { useEffect, useState } from "react";
import { Plus, Pencil, Trash2, Loader2 } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { AdminTitle, Modal, Field } from "@/pages/admin/AdminUI";
import ImageUpload from "@/components/ImageUpload";
import { toast } from "sonner";

const empty = { title: "", category: "Digital Marketing", excerpt: "", content: "", title_id: "", excerpt_id: "", content_id: "", featured_image: "", tags: [], author: "ADNEXA DIGITAL", published: true, seo_title: "", seo_description: "" };

export default function AdminBlog() {
  const [items, setItems] = useState(null);
  const [cats, setCats] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/admin/blog").then((r) => setItems(r.data)).catch(() => setItems([]));
  useEffect(() => { load(); api.get("/blog-categories").then((r) => setCats(r.data)).catch(() => {}); }, []);

  const openNew = () => { setEditing(null); setForm(empty); setOpen(true); };
  const openEdit = (b) => { setEditing(b); setForm({ ...empty, ...b, tags: b.tags || [] }); setOpen(true); };
  const save = async (e) => {
    e.preventDefault(); setSaving(true);
    try { if (editing) await api.put(`/admin/blog/${editing.id}`, form); else await api.post("/admin/blog", form); toast.success("Saved"); setOpen(false); load(); }
    catch (err) { toast.error(apiError(err)); }
    setSaving(false);
  };
  const del = async (b) => { if (!window.confirm("Delete article?")) return; await api.delete(`/admin/blog/${b.id}`); load(); };

  if (items === null) return <div className="flex justify-center py-20"><Loader2 className="animate-spin text-amber-400" size={36} /></div>;

  return (
    <div>
      <AdminTitle title="Blog" action={<button onClick={openNew} className="gold-btn text-sm" data-testid="admin-add-blog"><Plus size={16} /> New Article</button>} />
      <div className="card-lux overflow-x-auto">
        <table className="w-full text-sm min-w-[560px]" data-testid="admin-blog-table">
          <thead className="bg-black/30 text-slate-500 text-xs uppercase"><tr><th className="text-left px-5 py-3">Title</th><th className="text-left px-5 py-3">Category</th><th className="text-left px-5 py-3">Status</th><th className="text-right px-5 py-3">Actions</th></tr></thead>
          <tbody>
            {items.map((b) => (
              <tr key={b.id} className="border-t border-white/5" data-testid={`admin-blog-row-${b.slug}`}>
                <td className="px-5 py-3 text-white">{b.title}</td>
                <td className="px-5 py-3 text-slate-400">{b.category}</td>
                <td className="px-5 py-3">{b.published ? <span className="text-emerald-400 text-xs">Published</span> : <span className="text-slate-500 text-xs">Draft</span>}</td>
                <td className="px-5 py-3 text-right space-x-2">
                  <button onClick={() => openEdit(b)} className="text-slate-400 hover:text-amber-400" data-testid={`edit-blog-${b.slug}`}><Pencil size={16} /></button>
                  <button onClick={() => del(b)} className="text-slate-400 hover:text-red-400"><Trash2 size={16} /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Modal open={open} onClose={() => setOpen(false)} title={editing ? "Edit Article" : "New Article"} wide>
        <form onSubmit={save} className="space-y-4" data-testid="blog-form">
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Title (EN)"><input required className="input-lux" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="blog-title-field" /></Field>
            <Field label="Category"><select className="input-lux" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}>{cats.map((c) => <option key={c}>{c}</option>)}</select></Field>
          </div>
          <ImageUpload label="Featured Image" value={form.featured_image} onChange={(v) => setForm({ ...form, featured_image: v })} testid="blog-image" />
          <Field label="Excerpt (EN)"><textarea rows={2} className="input-lux resize-none" value={form.excerpt} onChange={(e) => setForm({ ...form, excerpt: e.target.value })} /></Field>
          <Field label="Content (EN, HTML allowed)"><textarea rows={5} className="input-lux resize-none" value={form.content} onChange={(e) => setForm({ ...form, content: e.target.value })} data-testid="blog-content-field" /></Field>
          <Field label="Title (ID)"><input className="input-lux" value={form.title_id} onChange={(e) => setForm({ ...form, title_id: e.target.value })} /></Field>
          <Field label="Content (ID, HTML allowed)"><textarea rows={4} className="input-lux resize-none" value={form.content_id} onChange={(e) => setForm({ ...form, content_id: e.target.value })} /></Field>
          <Field label="Tags (comma separated)"><input className="input-lux" value={form.tags.join(", ")} onChange={(e) => setForm({ ...form, tags: e.target.value.split(",").map((t) => t.trim()).filter(Boolean) })} /></Field>
          <label className="flex items-center gap-2 text-sm text-slate-300"><input type="checkbox" checked={form.published} onChange={(e) => setForm({ ...form, published: e.target.checked })} className="accent-amber-500" data-testid="blog-published" /> Published</label>
          <button disabled={saving} className="gold-btn w-full" data-testid="blog-save-btn">{saving ? <Loader2 className="animate-spin" size={18} /> : "Save Article"}</button>
        </form>
      </Modal>
    </div>
  );
}
